import { all, call, cancel, cancelled, fork, put, take, takeEvery } from 'redux-saga/effects';
import { backendUrl } from 'util/config';
import {
  DELETE_FILE,
  DOWNLOAD_ALL_FILES,
  DOWNLOAD_FILE,
  DOWNLOAD_FILE_SUCCESS,
  UPLOAD_FILE,
  STOP_UPLOAD_FILE,
  UPDATE_COMMENT,
  CREATE_COMMENT,
  UPDATE_PROJECT,
  UPDATE_TASK,
  UPDATE_ARTICLE,
  UPLOAD_FILE_FAILURE,
} from 'constants/ActionTypes';
import { createFileUploadChannel } from './fileUploadChannel';
import { uploadFailure, uploadProgress, uploadSuccess, uploadWhithoutFormSuccess } from 'actions/ui/filesActions';
import { deleteEntity, errorLimitResponse, fetchResource, loadEntities, showErrorMessage } from 'helpers/utilHelpers';

function* uploadFileSaga(channel, file, type, onProgress) {
  while (true) {
    const { progress = 0, err, success, response, errData } = yield take(channel);

    if (err) {
      errorLimitResponse(errData);
      yield put(uploadFailure(file, err));
      return;
    }
    if (success) {
      const fileModel = JSON.parse(response);
      const preparedFile = file;
      preparedFile.fileId = fileModel.fileId;
      if (type === 'withoutForm') {
        yield put(uploadWhithoutFormSuccess(fileModel));
      } else {
        yield put(uploadSuccess(file, fileModel));
      }
      return { ...fileModel, file: preparedFile };
    }

    if (onProgress) {
      onProgress(file, progress);
    }

    yield put(uploadProgress(file, progress));
  }
}

function* uploadProcess(action, channel) {
  try {
    const {
      payload: { type, onSuccessFunction, onProgress, file },
    } = action;

    const res = yield call(uploadFileSaga, channel, file, type, onProgress);

    if (onSuccessFunction) {
      onSuccessFunction(res);
    }
  } finally {
    if (yield cancelled()) {
      const {
        payload: { onCancelFunction },
      } = action;
      if (onCancelFunction) {
        onCancelFunction();
      }
    }
  }
}

function* uploadFile() {
  yield takeEvery(UPLOAD_FILE, function*(action) {
    const {
      payload: { file, entity, entityId, withoutEntity },
    } = action;

    let channel = yield call(createFileUploadChannel, backendUrl + '/api/files', file, {
      entity,
      entityId,
      withoutEntity,
    });
    const forkUpload = yield fork(uploadProcess, action, channel);
    yield take([
      UPLOAD_FILE_FAILURE,
      STOP_UPLOAD_FILE,
      UPDATE_COMMENT,
      CREATE_COMMENT,
      UPDATE_PROJECT,
      UPDATE_TASK,
      UPDATE_ARTICLE,
    ]);
    yield cancel(forkUpload);
    channel.close();
  });
}

function* deleteFile() {
  yield takeEvery(DELETE_FILE, deleteEntity);
}

function* downloadFile() {
  yield takeEvery(DOWNLOAD_FILE, loadEntities);
}

function* downloadAllFiles() {
  yield takeEvery(DOWNLOAD_ALL_FILES, fetchDownloadAllFiles);
}

function* fetchDownloadAllFiles(action) {
  try {
    const response = yield call(() =>
      fetchResource(action.payload.resource + '/all', 'GET', null, null, {
        entity: action.payload.entity,
      }),
    );
    yield put(action.payload.onSuccess(response));
  } catch (e) {
    yield showErrorMessage(action.payload.onErrorMessage);
  }
}

function* downloadFileSuccess() {
  yield takeEvery(DOWNLOAD_FILE_SUCCESS, (action) => {
    const { file, name, extension } = action.payload;
    // 2. Create blob link to download
    const url = window.URL.createObjectURL(new Blob([file]));
    const link = document.createElement('a');
    link.href = url;
    link.download = name + '.' + extension;
    // 3. Append to html page
    document.body.appendChild(link);
    // 4. Force download
    link.click();
    // 5. Clean up and remove the link
    link.parentNode.removeChild(link);
  });
}

export default function* rootSaga() {
  yield all([
    fork(uploadFile),
    fork(deleteFile),
    fork(downloadFile),
    fork(downloadFileSuccess),
    fork(downloadAllFiles),
  ]);
}
